import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import type { RowAction } from "./types";

interface DataTableActionsProps<TData> {
  row: TData;
  actions: RowAction<TData>[];
}

const variantStyles: Record<string, string> = {
  default: "text-gray-700 hover:bg-gray-100",
  danger: "text-red-600 hover:bg-red-50",
  success: "text-green-600 hover:bg-green-50",
};

export function DataTableActions<TData>({
  row,
  actions,
}: DataTableActionsProps<TData>) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    openUpward: boolean;
  }>({ top: 0, left: 0, openUpward: false });

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = 200; // estimated max height
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;

    setMenuPosition({
      top: openUpward ? rect.top : rect.bottom + 4,
      left: rect.right,
      openUpward,
    });
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  const visibleActions = actions.filter(
    (action) => !action.visible || action.visible(row)
  );

  if (visibleActions.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
      >
        <MoreVertical size={16} className="text-gray-400" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            style={{
              top: menuPosition.openUpward ? undefined : menuPosition.top,
              bottom: menuPosition.openUpward
                ? window.innerHeight - menuPosition.top + 4
                : undefined,
              left: menuPosition.left - 176, // 176 = w-44 = 11rem
            }}
          >
            {visibleActions.map((action) => {
              const style = variantStyles[action.variant || "default"];

              if (action.href) {
                const href = action.href(row);
                return (
                  <a
                    key={action.key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${style}`}
                  >
                    {action.icon && <action.icon size={14} />}
                    {action.label}
                  </a>
                );
              }

              return (
                <button
                  key={action.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    action.onClick(row);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${style}`}
                >
                  {action.icon && <action.icon size={14} />}
                  {action.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
